import React from "react";
import { MightierLogoSVG } from "@components/MightierLogoSvg";
import { Anchor, Box, Flex, Image, Stack, Text, Title } from "@mantine/core";
import { Outlet } from "@tanstack/react-router";
import { useColors } from "@utils/useColors";
import hipaaLogo from "src/assets/images/hipaa_verified_logo.png";

import cx from "./layout.module.css";
import { useTranslation } from "react-i18next";
import { staticLinks } from "@config/staticLinks";

import { useLegalUrls } from "../components/AccountActivateForm/formComponents/TermsOfService";
import type { MightierSupportedLanguage } from "@neuromotion-inc/mightier-ts-sdk";

interface FooterUrls {
  faqsUrl: string;
  contactUrl: string;
}

export const useFooterUrls = (): FooterUrls => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as MightierSupportedLanguage;
  return {
    faqsUrl: staticLinks.faqs(currentLanguage),
    contactUrl: staticLinks.contactPage(currentLanguage),
  };
};

export const StartPageLayout: React.FC = () => {
  return (
    <>
      <Outlet />
      <Footer />
    </>
  );
};

const OuterAnchor = Anchor.withProps({
  target: "_blank",
  rel: "noopener noreferrer",
});

const Footer: React.FC = () => {
  const colors = useColors();
  const { t } = useTranslation();
  const { faqsUrl, contactUrl } = useFooterUrls();

  return (
    <Stack className={cx.footer} bg={colors.royal100}>
      <Stack className={cx.links}>
        <Title className={cx.heading}>{t("startPageFooter.support")}</Title>
        <OuterAnchor className={cx.anchor} href={contactUrl}>
          {t("startPageFooter.familyCareTeam")}
        </OuterAnchor>
        <OuterAnchor className={cx.anchor} href={faqsUrl}>
          {t("startPageFooter.helpCenter")}
        </OuterAnchor>
        <p />
      </Stack>

      <Flex className={cx["social-medias"]} hiddenFrom="xs">
        <SocialMediaLinks />
      </Flex>

      <Flex className={cx.logos}>
        <MightierLogoSVG height={24} />
        <Image src={hipaaLogo} alt="HIPAA Verified" h="65px" w="123px" />
      </Flex>

      <Box hiddenFrom="xs">
        <LegalInfo />
      </Box>
    </Stack>
  );
};

const SocialMediaLinks: React.FC = () => {
  const { contactUrl } = useFooterUrls();
  return (
    <>
      <OuterAnchor className={cx.anchor} href={staticLinks.facebook}>
        <svg
          width="25"
          height="24"
          viewBox="0 0 25 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M24.726 12.0733C24.726 5.40541 19.3534 0 12.726 0C6.09853 0 0.725952 5.40541 0.725952 12.0733C0.725952 18.0994 5.11416 23.0943 10.851 24V15.5633H7.80408V12.0733H10.851V9.41343C10.851 6.38755 12.6425 4.71615 15.3835 4.71615C16.696 4.71615 18.0697 4.95195 18.0697 4.95195V7.92313H16.5566C15.066 7.92313 14.601 8.85386 14.601 9.80959V12.0733H17.9291L17.397 15.5633H14.601V24C20.3377 23.0943 24.726 18.0994 24.726 12.0733Z"
            fill="white"
          />
        </svg>
      </OuterAnchor>
      <OuterAnchor className={cx.anchor} href={staticLinks.x}>
        <svg
          width="25"
          height="24"
          viewBox="0 0 25 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_27_154)">
            <path
              d="M22.9543 0H2.49762C1.51929 0 0.725952 0.793333 0.725952 1.77167V22.2283C0.725952 23.2067 1.51929 24 2.49762 24H22.9543C23.9326 24 24.726 23.2067 24.726 22.2283V1.77167C24.726 0.793333 23.9326 0 22.9543 0ZM7.87929 20.4467H4.27095V8.98333H7.87929V20.445V20.4467ZM6.07595 7.39667C4.92762 7.39667 3.99595 6.52167 3.99595 5.325C3.99595 4.12833 4.92762 3.25333 6.07595 3.25333C7.22429 3.25333 8.15595 4.12833 8.15595 5.325C8.15595 6.52167 7.22429 7.39667 6.07595 7.39667ZM21.1826 20.4567H17.5743V14.1967C17.5743 12.35 16.7893 11.78 15.776 11.78C14.706 11.78 13.656 12.585 13.656 14.2433V20.4583H10.0476V8.99167H13.5176V10.58H13.5643C13.9126 9.875 15.1326 8.67 16.9943 8.67C19.0076 8.67 21.1826 9.865 21.1826 13.365V20.455V20.4567Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0_27_154">
              <rect
                width="24"
                height="24"
                fill="white"
                transform="translate(0.725952)"
              />
            </clipPath>
          </defs>
        </svg>
      </OuterAnchor>
      <OuterAnchor className={cx.anchor} href={contactUrl}>
        <svg
          width="25"
          height="22"
          viewBox="0 0 25 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19.5795 0.062561H23.2341L15.2499 9.18801L24.6427 21.6057H17.2882L11.5279 14.0744L4.93677 21.6057H1.27996L9.81988 11.845L0.809326 0.062561H8.35053L13.5573 6.94642L19.5795 0.062561ZM18.2968 19.4182H20.3219L7.25017 2.13512H5.07708L18.2968 19.4182Z"
            fill="white"
          />
        </svg>
      </OuterAnchor>
    </>
  );
};

const LegalInfo: React.FC = () => {
  const { t } = useTranslation();
  const { termsOfServiceUrl, privacyPolicyUrl } = useLegalUrls();

  return (
    <Text className={cx["legal-info"]}>
      <OuterAnchor href={privacyPolicyUrl}>
        {t("startPageFooter.privacyPolicy")}
      </OuterAnchor>
      {" | "}
      <OuterAnchor href={termsOfServiceUrl}>
        {t("startPageFooter.termsOfUse")}
      </OuterAnchor>
      {" | "}
      {t("startPageFooter.allRightsReserved")}
    </Text>
  );
};
